import React, { Component } from 'react';

import { web3FromAddress } from '@polkadot/extension-dapp';
import { ApiPromise, WsProvider } from '@polkadot/api';
import { decodeAddress } from '@polkadot/keyring';
import { u8aToHex, stringToHex } from '@polkadot/util';
import { Collection } from 'rmrk-tools';
import { Animated } from 'react-animated-css';

import { pinFileToIPFS } from './PinFileToIPFS';
import { pinJSONToIPFS } from './PinJSONToIPFS';
import { getBlockchainLag } from './GetBlockchainLag';
import axios from 'axios';
import slugify from 'slugify';
import ProgressionPopup from './ProgressionPopup';

// Form to mint a new RMRK collection
class RmrkMintForm extends Component {
  constructor(props) {
    super(props);

    // Sets default test values in state
    this.state = {
      name: '',
      description: '',
      maxSupply: '',
      symbol: '',
      submitDisabled: false,
      errorMessage: '',
      progressMessage: '',
      modalOpened: false,
      allowModalClose: false,
      redirectToGallery: false,
      error: {
        name: false,
        description: false,
        maxSupply: false,
        symbol: false
      }
    };

    this.handleInputChange = this.handleInputChange.bind(this);
    this.fileInput = React.createRef();
    this.handleSubmit = this.handleSubmit.bind(this);
    this.closePopup = this.closePopup.bind(this);
    this.checkIfCollectionsAreSynced = this.checkIfCollectionsAreSynced.bind(this);
  }

  closePopup() {
    if (this.state.redirectToGallery) {
      this.props.changeVisibilities({ collectionGallery: true, mint: false })
    }
    this.setState({
      modalOpened: false,
      allowModalClose: false,
      falseredirectToGallery: false
    })
  }

  resetForm() {
    this.setState({
      name: '',
      description: '',
      maxSupply: '',
      symbol: ''
    });
  }

  handleInputChange(event) {
    this.setState({
      [event.target.name]: event.target.value
    });
  }

  handleSubmit(event) {
    // Apply filter on symbol value
    this.setState({
      symbol: slugify(this.state.symbol, '_').toUpperCase().replace(/[^a-zA-Z0-9]+/g, '_')
    }, async () => {
      if (await this.validateForm()) this.mintRmrkCollection();
    });
    event.preventDefault();
  }

  async validateForm() {
    var message = '';
    var errors = {
      name: false,
      description: false,
      maxSupply: false,
      symbol: false
    }

    // Collection name must not be empty
    if (this.state.name.length == 0) {
      errors.name = true;
      message += 'Collection name must be filled. '
    }

    // Max supply must be positive integer
    if (!/^(-|\+)?(\d+)$/.test(this.state.maxSupply) || parseInt(this.state.maxSupply) < 0 || parseInt(this.state.maxSupply) > 9999999999999999n) {
      errors.maxSupply = true;
      message += 'Max supply must be a positive integer. '
    } else {
      // If number is valid, set parsed value in state
      this.setState({ maxSupply: Number(this.state.maxSupply) });
    }

    // Symbol must not be empty
    if (this.state.symbol.length == 0) {
      errors.symbol = true;
      message += 'Symbol must be filled. '
    }

    // Control on preexisting symbols
    try {
      var existingCollectionSymbols = await axios.get(process.env.REACT_APP_API_URL + "api/v1/collections?owner=" + this.props.accountAddress)
        .then(function (response) {
          var collections = response.data.data;
          var existingCollectionSymbols = Array();

          // Extracts existing collection symbols
          for (var index in collections) {
            existingCollectionSymbols[index] = collections[index].id.split('-')[1];
          }
          return existingCollectionSymbols;
        })
        .catch(function (error) {
          throw ("Error while fetching collections. ")
        });

      if (existingCollectionSymbols.includes(this.state.symbol)) {
        errors.symbol = true;
        message += 'Symbol is already used for another collection. '
      }
    } catch (error) {
      message += error;
    }

    if (message != '') {
      this.setState({ error: errors, errorMessage: message, progressMessage: '' });
      return false;
    } else {
      this.setState({ error: errors, errorMessage: '' });
      return true;
    }
  }

  async mintRmrkCollection() {

    // Breaks if no account
    if (this.props.accountAddress == '') { alert("No account found, please install Polkadot.js extension"); return; }

    // Disables submit
    this.setState({ submitDisabled: true, modalOpened: true });

    try {
      // Upload collection image to IPFS
      var progressMessage = "Step 1 - Mint collection - Uploading collection image to IPFS...";
      this.setState({ progressMessage: progressMessage });

      var imageIpfsHash = process.env.REACT_APP_DEFAULT_COLLECTION_IMAGE_IPFS;
      if (typeof this.fileInput.current.files[0] !== 'undefined') {
        var imageUploadResponse = await pinFileToIPFS(this.fileInput.current.files[0]);
        if (typeof imageUploadResponse.data === 'undefined') {
          throw ("Error while uploading image to IPFS");
        } else {
          imageIpfsHash = imageUploadResponse.data.IpfsHash;
        }
      }

      // Generate metadata JSON for collection
      const collectionMetadata =
      {
        description: this.state.description,
        image: "ipfs://ipfs/" + imageIpfsHash
      }

      // Debug logs
      console.info("Collection metadata:");
      console.dir(collectionMetadata);

      // Upload collection metadata to IPFS
      progressMessage = "Step 2 - Mint collection - Uploading collection metadata to IPFS...";
      this.setState({ progressMessage: progressMessage });

      var metadataUploadResponse = await pinJSONToIPFS(collectionMetadata);
      if (typeof metadataUploadResponse.data === 'undefined') {
        throw ('Error while uploading metadata to IPFS');
      }

      // Generate collection
      const collection = new Collection(
        0, // Block (should be empty before mint)
        this.state.name, // Collection name
        Number(this.state.maxSupply), // Max supply
        this.props.accountAddress, // Issuer (address)
        this.state.symbol.toUpperCase().replace(/[^a-zA-Z0-9]+/g, '_'), // Symbol (name uppercased with special chars changed to '_')
        Collection.generateId(u8aToHex(decodeAddress(this.props.accountAddress)), this.state.symbol), // Generate ID
        "ipfs://ipfs/" + metadataUploadResponse.data.IpfsHash //Metadata URL (using new ipfs hash)
      );

      // Debug logs
      console.info("Collection data:");
      console.dir(collection);

      // Generate RMRK
      progressMessage = "Step 3 - Mint Collection - Generating RMRK for collection";
      this.setState({ progressMessage: progressMessage });

      const rmrk = collection.mint();

      // Debug logs
      console.info("Generated RMRK for collection: " + rmrk);

      // Check blockchain lag
      var blockchainLag = await getBlockchainLag();
      if (blockchainLag > process.env.REACT_APP_BLOCKCHAIN_LAG_LIMIT) {
        throw ('Blockchain lag is too heavy (> ' + process.env.REACT_APP_BLOCKCHAIN_LAG_LIMIT + ' blocks), aborting process');
      }

      // Get API and injector from extension
      const wsProvider = new WsProvider(process.env.REACT_APP_WSPROVIDER_URL);
      const api = await ApiPromise.create({ provider: wsProvider });
      const injector = await web3FromAddress(this.props.accountAddress);

      // Generating extrinsic
      const remarkExtrinsic = api.tx.system.remark(stringToHex(rmrk));

      progressMessage = "Step 4 - Mint Collection - Requesting signature for collection RMRK";
      this.setState({ progressMessage: progressMessage });

      await remarkExtrinsic.signAndSend(this.props.accountAddress, { signer: injector.signer }, async ({ status }) => {

        if (status.isBroadcast) {
          this.resetForm();
          progressMessage = "Step 5 - Mint Collection - Broadcasted transaction, waiting for inclusion";
          this.setState({ progressMessage: progressMessage });
        } else if (status.isInBlock) {

          console.info(`Completed at block hash #${status.asInBlock.toString()}`);
          progressMessage = "Step 6 - Mint Collection - Transaction included in block, waiting for blockchain sync...";
          this.setState({ progressMessage: progressMessage });

          // Checks if collections are synced
          this.checkIfCollectionsAreSynced(collection.id, this.props.accountAddress, () => {
            this.props.updateSelectsFromAPI();
            progressMessage = "Step SUCCESS - Mint Collection - Collection successfully created";
            this.setState({ progressMessage: progressMessage });
            this.setState({ submitDisabled: false, allowModalClose: true, redirectToGallery: true });
          }, (error) => {
            progressMessage = "Step PARTIAL (NO BLOCKCHAIN SYNC) - Mint Collection - " + error;
            this.setState({ progressMessage: progressMessage });
            this.setState({ submitDisabled: false, allowModalClose: true, redirectToGallery: true });
          });

        } else {
          console.info(`Current status: ${status.type}`);
        }
      }).catch((error) => {
        throw ('Error during transaction: ', error);
      });

    } catch (error) {

      // Enables submit
      this.setState({ submitDisabled: false, allowModalClose: true });

      // Logs error
      console.warn("Exception: " + error);
      progressMessage = "Step ERROR - Mint Collection - Process aborted: " + error;
      this.setState({ progressMessage: progressMessage });

    }

  }

  // Routine to check if collections are synced
  checkIfCollectionsAreSynced(collectionId, accountAddress, callback, error) {

    var callsPerformedToCheckSync = 0;
    var collectionSyncInterval = setInterval(collectionSyncCheck, process.env.REACT_APP_CHECK_SYNC_TIMER_MS);

    async function collectionSyncCheck() {
      console.info("Check for collection sync #" + ++callsPerformedToCheckSync);

      await axios.get(process.env.REACT_APP_API_URL + "api/v1/collections?owner=" + accountAddress)
        .then(function (response) {
          var collections = response.data.data;

          // Checks for collection presence
          for (var index in collections) {
            if (collections[index].id == collectionId) {
              clearInterval(collectionSyncInterval);
              callback();
              return true;
            }
          }

          // Throw exception is number of polls exceeded
          if (callsPerformedToCheckSync >= process.env.REACT_APP_CHECK_SYNC_NB_RETRIES) {
            clearInterval(collectionSyncInterval);
            error("Collection is not synced (timeout) but was successfully sent to blockchain, please try to reload page later")
          }
          return false;
        });
    }

  }

  render() {
    return (
      <Animated animationIn="fadeIn" animationInDuration={300} isVisible={this.props.display}>
        <div id="mint-component" className={`card ${!this.props.display ? ' d-none' : ''}`}>
          <div className="row">
            <div className="col-lg-12">
              <div className="page-heading page-heading--overlay" style={{ backgroundImage: "url(https://everdreamsoft.mypinata.cloud/ipfs/" + process.env.REACT_APP_DEFAULT_COLLECTION_IMAGE_IPFS + ")", backgroundColor: "rgba(0, 0, 0, 0.6)" }}>
                <div className="container">
                  <div className="row">
                    <div className="col-12">
                      <div className="alc-staff-inner post__meta-block post__meta-block--top posts__item--card posts__item">
                        <div className="posts__category">
                          <span className="label posts__cat-label posts__cat-label--kusama">{process.env.REACT_APP_CHAIN_NAME}</span>
                          <span className="label posts__cat-label posts__cat-label--category-1">RMRK</span>
                        </div>
                        <header className="alc-staff__header">
                          <h1 className="page-heading__title">
                            <span className="alc-staff__header-last-name">
                              Create a new collection
                          </span>
                          </h1>
                          <span className="alc-staff__header-role">
                            <h5 className="alc-staff__header-role-item">
                              You will be able to mint NFTs in this collection right after
                          </h5>
                          </span>
                        </header>
                        <button className="btn btn-primary" onClick={() => this.props.changeVisibilities({ collectionGallery: true, mint: false })}>Back to collection list</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <form onSubmit={this.handleSubmit}>
                <div className="card__content row">
                  <div className="col-12 alert alert-warning" hidden={this.state.errorMessage == ''}>{this.state.errorMessage}</div>
                  <div className={`col-12 col-sm-6 form-group${this.state.error.name ? ' has-error' : ''}`}>
                    <label>Collection name:</label>
                    <input className="form-control" type="text" name="name" value={this.state.name} onChange={this.handleInputChange} placeholder="e.g. My super collection" />
                  </div>
                  <div className={`col-12 col-sm-3 form-group${this.state.error.maxSupply ? ' has-error' : ''}`}>
                    <label>Max supply:</label>
                    <input className="form-control" type="text" name="maxSupply" value={this.state.maxSupply} onChange={this.handleInputChange} placeholder="e.g. 100" />
                  </div>
                  <div className={`col-12 col-sm-3 form-group${this.state.error.symbol ? ' has-error' : ''}`}>
                    <label>Symbol:</label>
                    <input className="form-control" type="text" name="symbol" value={this.state.symbol} onChange={this.handleInputChange} placeholder="e.g. COLL" />
                  </div>
                  <div className={`col-12 form-group${this.state.error.description ? ' has-error' : ''}`}>
                    <label>Description:</label>
                    <textarea className="form-control" name="description" value={this.state.description} onChange={this.handleInputChange} placeholder="e.g. My super collection description" />
                  </div>
                  <div className="col-12 form-group">
                    <label>Image:</label>
                    <input className="form-control btn" type="file" name="image" ref={this.fileInput} />
                  </div>
                  <div className="col-12 form-group">
                    <input className="btn btn-ksmgallery" type="submit" disabled={this.state.submitDisabled} value="Sign and send new collection transaction" />
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        <ProgressionPopup
          popupTitle="Creating collection..."
          modalOpened={this.state.modalOpened}
          allowModalClose={this.state.allowModalClose}
          progressMessage={this.state.progressMessage}
          closePopup={this.closePopup}
        />

      </Animated>
    )
  }
}

export default RmrkMintForm;